import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";

import { getSettings, putSettings, type Settings } from "@/lib/api";

interface SettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: React.ReactNode;
  onSaved?: (settings: Settings) => void;
}

/** SettingsSheet holds a user's org-scoped knobs: watchlist, capital,
 *  lookback, default benchmark, and per-ticker benchmark overrides.
 *  Process-wide settings (cache TTL, tracker cadence, scanner tuning,
 *  quality gates) moved to /admin and live in ProcessSettingsForm.
 *  Non-admin users never need this sheet to see or change
 *  process-wide values — they're gated at the server anyway. */
export function SettingsSheet({ open, onOpenChange, trigger, onSaved }: SettingsSheetProps) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [watchlist, setWatchlist] = useState("");
  const [capital, setCapital] = useState("");
  const [lookback, setLookback] = useState("");
  const [defaultBenchmark, setDefaultBenchmark] = useState("");
  const [overridesText, setOverridesText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void getSettings()
      .then((s) => {
        setSettings(s);
        setWatchlist(s.DefaultWatchlist.join(" "));
        setCapital(String(s.DefaultCapital));
        setLookback(s.DefaultLookback);
        setDefaultBenchmark(s.DefaultBenchmark);
        setOverridesText(
          Object.entries(s.BenchmarkOverrides ?? {})
            .map(([k, v]) => `${k}=${v}`)
            .join("\n")
        );
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [open]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const tickers = watchlist
        .split(/[\s,]+/)
        .map((t) => t.trim().toUpperCase())
        .filter(Boolean);
      const overrides: Record<string, string> = {};
      for (const line of overridesText.split("\n")) {
        const m = line.trim().match(/^([A-Z0-9.\-]+)\s*=\s*([A-Z0-9.\-]+)$/i);
        if (m) overrides[m[1]!.toUpperCase()] = m[2]!.toUpperCase();
      }
      const payload: Record<string, unknown> = {
        default_watchlist: tickers,
        default_capital: Number(capital),
        default_lookback: lookback,
        default_benchmark: defaultBenchmark,
        benchmark_overrides: overrides,
      };
      const updated = await putSettings(payload);
      setSettings(updated);
      onSaved?.(updated);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Settings</SheetTitle>
          <SheetDescription>
            Your org-scoped watchlist + defaults. Process-wide controls
            live on the <code>/admin</code> page (platform admins only).
          </SheetDescription>
        </SheetHeader>
        <Separator className="my-3" />

        {error && <p className="text-sm text-rose-500 mb-3">{error}</p>}

        <div className="space-y-4 px-4">
          <div>
            <Label htmlFor="wl" className="text-xs">
              Default watchlist (space separated)
            </Label>
            <Input
              id="wl"
              value={watchlist}
              onChange={(e) => setWatchlist(e.target.value)}
              className="font-mono"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cap" className="text-xs">
                Default capital
              </Label>
              <Input
                id="cap"
                type="number"
                value={capital}
                onChange={(e) => setCapital(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="lb" className="text-xs">
                Default lookback
              </Label>
              <Input
                id="lb"
                value={lookback}
                onChange={(e) => setLookback(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="bench" className="text-xs">
              Default benchmark
            </Label>
            <Input
              id="bench"
              value={defaultBenchmark}
              onChange={(e) => setDefaultBenchmark(e.target.value)}
              placeholder="IWM"
            />
          </div>
          <div>
            <Label htmlFor="overrides" className="text-xs">
              Per-ticker benchmark overrides (one per line, FORMAT: TICKER=BENCH)
            </Label>
            <Textarea
              id="overrides"
              value={overridesText}
              onChange={(e) => setOverridesText(e.target.value)}
              rows={4}
              className="font-mono text-xs"
              placeholder="QBTS=QTUM"
            />
          </div>
        </div>
        <SheetFooter className="mt-4">
          <Button disabled={saving || !settings} onClick={save}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
